import React, { useState, useEffect } from "react";
import "./FilesForm.css";
import Confirm from "../../../molecules/Confirm";
import { trackPromise } from "react-promise-tracker";
import { v4 } from "uuid";
import { apiDeleteFile, apiPostFiles } from "../../../../utils/queries";
import config from "../../../../config";
import { displayBankType } from "../../../../utils/constants";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrashAlt, faPaperclip, faInfoCircle, faFile } from "@fortawesome/free-solid-svg-icons";

import { Label } from "reactstrap";

// Mirrors `FileUploadValidator` on the server. Update both together.
const ALLOWED_EXTENSIONS = (config.ALLOWED_ATTACHMENT_EXTENSIONS || [".pdf", ".jpg", ".jpeg", ".png"])
  .map((e) => e.toLowerCase());
const MAX_ATTACHMENT_SIZE = config.MAX_ATTACHMENT_SIZE || 10 * 1024 * 1024;

const formatBytes = (bytes) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, "")} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1).replace(/\.0$/, "")} KB`;
  return `${bytes} bytes`;
};

const allowedExtensionsLabel = ALLOWED_EXTENSIONS
  .map((e) => e.replace(/^\./, "").toUpperCase())
  .join(", ");

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/* ── Reusable Section Card ── */
const SectionCard = ({ icon, title, subtitle, children }) => (
  <div className="emp-section-card">
    <div className="emp-section-card__header">
      <div className="emp-section-card__title">
        <FontAwesomeIcon icon={icon} className="emp-section-card__icon" />
        <div>
          <span>{title}</span>
          {subtitle && <small className="emp-section-card__subtitle">{subtitle}</small>}
        </div>
      </div>
    </div>
    <div className="emp-section-card__body">{children}</div>
  </div>
);

export const FilesForm = (props) => {
  const [toggleConfirmFileDelete, setToggleConfirmFileDelete] = useState(false);
  const [selectedFile, setSelectedFile] = useState({});
  const [selectedBankDataId, setSelectedBankDataId] = useState("");



  const onDeleteFile = async (file) => {
    setSelectedFile(file);
    setToggleConfirmFileDelete(!toggleConfirmFileDelete);
  };

  const selectedFilesChanged = async (e) => {
    const picked = e.target.files;
    // Reset the input early so picking the same file again still fires
    // a change event later.
    const resetInput = () => { e.target.value = ""; };

    if (!picked || picked.length === 0) {
      return;
    }

    if (selectedBankDataId === "") {
      addAlert("Please select a bank detail before uploading files.", "danger");
      resetInput();
      return;
    }

    // Client-side mirror of FileUploadValidator on the server. We still let
    // the API reject too, but failing fast spares the user a round-trip.
    const errors = [];
    for (let i = 0; i < picked.length; i++) {
      const file = picked[i];
      const ext = (file.name.match(/\.[^.]+$/) || [""])[0].toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        errors.push(`"${file.name}" must be a ${allowedExtensionsLabel} file.`);
        continue;
      }
      if (file.size <= 0) {
        errors.push(`"${file.name}" is empty and cannot be uploaded.`);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_SIZE) {
        errors.push(
          `"${file.name}" is larger than the ${formatBytes(MAX_ATTACHMENT_SIZE)} limit.`
        );
        continue;
      }

      // Duplicate-name check inside the same bank account.
      const existingList = props?.fileList ?? [];
      const isDuplicate = existingList.some(
        (existing) =>
          existing.name === file.name && existing.bankDataId === selectedBankDataId
      );
      if (isDuplicate) {
        const bankDataDescription = selectedBankDataId === 0
          ? "Other"
          : showBankDataDetails(selectedBankDataId);
        errors.push(`"${file.name}" has already been uploaded to ${bankDataDescription}.`);
      }
    }

    if (errors.length > 0) {
      errors.forEach((message) => addAlert(message, "danger"));
      resetInput();
      return;
    }

    const form = new FormData();
    form.append("BankDataId", selectedBankDataId);
    form.append("RequestId", props.accountId);
    for (let i = 0; i < picked.length; i++) {
      form.append("file", picked[i]);
    }

    try {
      const response = await trackPromise(apiPostFiles(form, props.token));
      const uploaded = response?.data;
      if (!Array.isArray(uploaded) || uploaded.length === 0) {
        // Server accepted but returned no files — treat as a no-op.
        resetInput();
        return;
      }
      props.setFileList(
        props?.fileList ? [...props.fileList, ...uploaded] : uploaded
      );

      const bankDataDescription = selectedBankDataId === 0
        ? "Other"
        : showBankDataDetails(selectedBankDataId);
      const noun = uploaded.length === 1 ? "File" : `${uploaded.length} files`;
      addAlert(`${noun} uploaded to ${bankDataDescription}.`, "success");
    } catch (error) {
      console.error("Upload error:", error);
      const responseErrors = error?.response?.data?.errors;
      if (Array.isArray(responseErrors) && responseErrors.length > 0) {
        responseErrors.forEach((err) =>
          addAlert(err.message || "Invalid file.", "danger")
        );
      } else if (error?.response?.status === 413) {
        addAlert(
          `One of the files is larger than the ${formatBytes(MAX_ATTACHMENT_SIZE)} limit.`,
          "danger"
        );
      } else {
        addAlert(
          "Uploading the file failed. Please try again or contact Hafnia.",
          "danger"
        );
      }
    } finally {
      resetInput();
    }
  };

  const onDeleteFileConfirmed = async () => {
    try {
      await apiDeleteFile(props.token, selectedFile.fileName);
      addAlert("File deleted", "success");
      props.setFileList(
        props?.fileList.filter(
          (item) => item.fileName !== selectedFile.fileName
        )
      );
    } catch (error) {
      addAlert("Could not delete file. Please contact Hafnia.", "error");
    }
  };

  const addAlert = (message, color, timeout = 7000) => {
    if (props.addAlert) {
      props.addAlert(message, color, timeout);
    }
  };

  const showBankDataDetails = (bankDataId) => {
    const bankDataList = props.bankDataList || [];
    const bankData = bankDataList.find(bd => bd.id === bankDataId);
    if (!bankData) return `Bank Account #${bankDataId}`;

    const bankName = bankData.bank || "N/A";
    const purpose = bankData.purpose || "N/A";
    const type = displayBankType(bankData.type) || "N/A";

    return `${bankName} - ${type} - ${purpose}`;
  };

  const getFilesForBankData = (bankDataId) => {
    return props?.fileList?.filter((file) => file?.bankDataId === bankDataId) || [];
  };

  return (
    <SectionCard
      icon={faPaperclip}
      title="Files"
      subtitle={`Select the bank detail this document relates to, then attach a sample invoice or a bank letter (${allowedExtensionsLabel}, max ${formatBytes(MAX_ATTACHMENT_SIZE)} per file). All fields marked with * are required.`}
    >

      {/* Bank Selection and File Upload */}
      <div className="files-upload">
        <div className="files-upload__field">
          <Label className="files-upload__label">Bank Detail *</Label>
          <div className="files-bank-options">
            {(props.bankDataList || []).map((bankData) => {
              const isActive = selectedBankDataId === bankData.id;
              return (
                <button
                  type="button"
                  key={bankData.id}
                  className={`files-bank-option${isActive ? " files-bank-option--active" : ""}`}
                  onClick={() => setSelectedBankDataId(bankData.id)}
                >
                  <span className="files-bank-option__name">{bankData.bank || "Bank account"}</span>
                  <span className="files-bank-option__meta">
                    {bankData.type && <span className="files-bank-option__chip">{displayBankType(bankData.type)}</span>}
                    {bankData.purpose && <span className="files-bank-option__chip">{bankData.purpose}</span>}
                  </span>
                </button>
              );
            })}
            <button
              type="button"
              className={`files-bank-option${selectedBankDataId === 0 ? " files-bank-option--active" : ""}`}
              onClick={() => setSelectedBankDataId(0)}
            >
              <span className="files-bank-option__name">Other</span>
              <span className="files-bank-option__meta">
                <span className="files-bank-option__chip">Not linked to a bank detail</span>
              </span>
            </button>
          </div>
        </div>

        {/* Uploaded files */}
        {props?.fileList && props.fileList.length > 0 && (
          <div className="rd-file-list">
            {props.fileList.map((file, index) => {
              const date = formatDate(file.uploadedAt);
              const source = file.bankDataId === 0 ? "Other" : showBankDataDetails(file.bankDataId);
              const meta = [source, date].filter(Boolean).join(" · ");
              return (
                <div key={index} className="rd-file-item">
                  <div className="rd-file-item__icon">
                    <FontAwesomeIcon icon={faFile} />
                  </div>
                  <div className="rd-file-item__info">
                    <a href={file.url} target="_blank" rel="noopener noreferrer" className="rd-file-item__name">
                      {file.displayName}
                    </a>
                    {meta && <span className="rd-file-item__meta">{meta}</span>}
                  </div>
                  <button
                    type="button"
                    className="rd-file-item__delete"
                    onClick={() => onDeleteFile(file)}
                    title="Delete"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <Label htmlFor={"file-upload-" + props.name} className="files-dropzone">
          <span className="files-dropzone__icon">
            <FontAwesomeIcon icon={faPlus} />
          </span>
          <span className="files-dropzone__text">Add File</span>
          <span className="files-dropzone__hint">
            {allowedExtensionsLabel} · max {formatBytes(MAX_ATTACHMENT_SIZE)} per file
          </span>
        </Label>
        <input
          id={"file-upload-" + props.name}
          type="file"
          accept={ALLOWED_EXTENSIONS.join(",")}
          onChange={selectedFilesChanged}
          multiple
        />
      </div>

      <Confirm
        onConfirm={onDeleteFileConfirmed}
        text={`Are you sure you want to delete "${selectedFile?.fileName || "this file"}"?`}
        toggle={toggleConfirmFileDelete}
        setToggle={setToggleConfirmFileDelete}
      ></Confirm>
    </SectionCard>
  );
};
